import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPage,
} from "next";
import { TemplateText, Text } from "src/presentation/components/atoms";
import { MainLayout } from "src/presentation/layouts";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { settings } from "@config/settings";
import { localesParserDictionary } from "@utils/locales-parser";
import { useEffect, useState } from "react";
import { Solucion } from "@models/solucion";
import { EntitiesFetcherService } from "@services/entities-fetcher";
import { useSoluciones } from "src/context/useSoluciones";
import {
  SolucionesDropdown,
  SolucionThumbnail,
} from "src/presentation/components/organisms";
import styles from "./soluciones-page.module.scss";
import { useTranslation } from "next-i18next";
import { NextSeo } from "next-seo";
import { MainRouter } from "@config/urls";
import { useRouter } from "next/router";
import { useCultivos } from "src/context/useCultivos";
import { useGruposDecultivo } from "src/context/useGruposDeCultivo";
import { GrupoDeCultivo } from "@models/grupo-de-cultivo";
import { Cultivo } from "@models/cultivos";

interface SolucionesFilter {
  grupo?: GrupoDeCultivo | null;
  cultivo?: Cultivo | null;
  solucion?: Solucion | null;
}

const SolucionesPage: NextPage = (props: any) => {
  const { country } = props;
  const soluciones = useSoluciones();
  const cultivos = useCultivos();
  const gruposDeCultivo = useGruposDecultivo();
  const { t } = useTranslation();
  const router = useRouter();
  const { query } = router;

  const [filters, setFilters] = useState<SolucionesFilter>({
    grupo: null,
    cultivo: null,
    solucion: null,
  });
  const [solucionesFiltered, setSolucionesFiltered] = useState<Array<any>>([]);

  useEffect(() => {
    const { cultivo, grupo, solucion } = query;
    let cultivoSelected = cultivo
      ? cultivos.find((cult) => `${cult.id}` === cultivo)
      : null;
    let grupoSelected = grupo
      ? gruposDeCultivo.find((group) => `${group.id}` === grupo)
      : null;
    let solucionSelected = solucion
      ? soluciones.find((sol) => `${sol.id}` === solucion)
      : null;
    const newFilters: SolucionesFilter = {
      cultivo: cultivoSelected || null,
      grupo: grupoSelected || null,
      solucion: solucionSelected || null,
    };
    setFilters(newFilters);
  }, [cultivos, gruposDeCultivo, query, soluciones]);

  useEffect(() => {
    let combinaciones = soluciones.map((solucion) => {
      return solucion.combinaciones_de_productos.map((combinacion) => {
        return {
          solucion: solucion.nombre,
          descripcion: solucion.descripcion,
          combinacion: combinacion,
        };
      });
    });

    let reduced = combinaciones
      .reduce((a, b) => a.concat(b))
      .filter((comb: any) => {
        if (!filters.cultivo) return true;
        return comb.combinacion.cultivos.indexOf(filters.cultivo.id) >= 0;
      })
      .filter((comb: any) => {
        if (!filters.grupo) return true;
        return comb.combinacion.cultivos.find((c: any) => {
          return (
            cultivos.find((x: any) => x.id === c)?.grupo_de_cultivo ===
            filters.grupo?.id
          );
        });
      })
      .filter((comb) => {
        if (!filters.solucion) return true;
        return comb.solucion === filters.solucion.nombre;
      });

    setSolucionesFiltered(reduced);
  }, [cultivos, filters, soluciones]);

  const handleDropdownChange = (newFilters: any) => {
    setFilters(newFilters);
  };
  return (
    <>
      <NextSeo
        title={t("SEOMeta.SolucionesView.title") || ""}
        description={t("SEOMeta.SolucionesView.description") || ""}
      />
      <MainLayout
        breadcrumb={[
          {
            label: t("urls.home"),
            url: MainRouter.home.generatePath(),
          },
          {
            label: t("urls.soluciones"),
            url: `/${country}${MainRouter.soluciones.generatePath()}`,
          },
        ]}
      >
        <div className={styles.root}>
          <section
            style={{
              backgroundImage: `linear-gradient(359deg,rgba(0,0,0,.66),hsla(0,0%,100%,.15)),url(/static/images/soluciones-manvert-bioestimulantes.webp)`,
            }}
            className={styles.header}
          >
            <Text as="h1">{t("SolucionesView.heading")}</Text>
            <div className={styles.dropdowncontainer}>
              <SolucionesDropdown
                initialFilters={filters}
                onChange={handleDropdownChange}
              />
            </div>
          </section>
          <section className={styles.solucionesSection}>
            <TemplateText template="normal">
              {solucionesFiltered
                ? solucionesFiltered.length
                    .toString()
                    .concat(" ")
                    .concat(
                      solucionesFiltered.length > 1
                        ? t("SolucionesView.counterPlural")
                        : t("SolucionesView.counterSingular")
                    )
                : t("SolucionesView.counterZero")}
            </TemplateText>
            {solucionesFiltered
              .filter((solucion) => solucion.solucion !== "")
              .map((solucion) => {
                return (
                  <SolucionThumbnail
                    key={
                      solucion.solucion +
                      "-" +
                      JSON.stringify(solucion.combinacion.productos)
                    }
                    nombre={solucion.solucion}
                    observaciones={solucion.descripcion}
                    productos={solucion.combinacion.productos}
                    country={country}
                  />
                );
              })}
          </section>
        </div>
      </MainLayout>
    </>
  );
};

export async function getServerSideProps(
  Props: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<any>> {
  const { locale, req, query, res } = Props;
  const host = `https://${req.headers.host}`;
  const { country } = query;

  const countryCode = country?.toString() || settings.DEFAULT_COUNTRY;

  const opts = {
    locale: localesParserDictionary[locale || settings.DEFAULT_LANG],
    country: countryCode,
  };

  const entities = await new EntitiesFetcherService().getEntities({
    ...opts,
    soluciones: true,
    familias: true,
    cultivos: true,
    productos: true,
    productosGenericos: true,
  });

  return {
    props: {
      country: countryCode,
      host: host,
      productosGenericos: JSON.stringify(entities.productosGenericosData),
      productosData: JSON.stringify(entities.productosData),
      cultivosData: JSON.stringify(entities.cultivosData),
      familiasData: JSON.stringify(entities.familiasData),
      solucionesData: JSON.stringify(entities.solucionesData),
      ...(await serverSideTranslations(locale || settings.I18N_FALLBACK, [
        "common",
      ])),
    },
  };
}

export default SolucionesPage;
